// src/utils/adminDataErrorTable.ts

import { AdminDataErrorViewResponse } from "@/types/adminDataErrorReponse";
import { formatOneDecimal, formatValueNoDecimals } from "../../utils/formatTwoDecimals";

// ---- small helpers (reused) ----
export const parseNumberOrText = (value: any) => {
  if (value === null || value === undefined) return "";
  const v = String(value).trim();
  if (v === "-" || v === "") return "";
  const num = Number(v);
  return Number.isFinite(num) ? num : v; // allow text if needed
};

export const toFieldName = (key: string) => {
  if (!key) return key;
  const parts = key.split(" ");
  parts[0] = parts[0].charAt(0).toLowerCase() + parts[0].slice(1);
  return parts.join("").replace(/[^\w]/g, "");
};

// ---- column builder ----
export const buildAdminErrorColumns = () => {
  const existingCols: any[] = [
    "Approval status",
    "Error Description",
    "Approver",
    "MRP controller",
    "MRP controller desc",
    "BCP Flag",
    "Material Plant",
    "Material Number",
    "Material Description",
    "End User action",
    "Reason",
    "Comments",
    {
      groupHeaderName: "End User proposal",
      marryChildren: true,
      children: [
        "Total consolidated Safety Stock (weeks)-EU",
        "Safety Time (weeks)-EU",
        "Coverage Profile (weeks)-EU",
        "Safety Stock (qty)-EU",
      ],
    },
    {
      groupHeaderName: "Consolidated Safety Stock (weeks)",
      marryChildren: true,
      children: ["IO Consolidated SS(weeks)", "S4 Consolidated SS(weeks)"],
    },
    {
      groupHeaderName: "Comparisons",
      marryChildren: true,
      children: ["EU proposal vs IO %", "EU proposal vs S4 %"],
    },
  ];

  const ensureCol = (header: string): any => {
    const field = toFieldName(header);
    return { field, headerName: header };
  };

  const columnDefs: any[] = [];
  existingCols.forEach((entry) => {
    if (typeof entry === "string") {
      columnDefs.push(ensureCol(entry));
    } else {
      const { groupHeaderName, children, marryChildren = true } = entry;
      const childDefs = children.map((h: string) => ensureCol(h));
      columnDefs.push({
        headerName: groupHeaderName,
        marryChildren,
        children: childDefs,
      });
    }
  });

  return columnDefs;
};

// ---- row/calculation builder ----
export const buildAdminErrorRows = (
  source: AdminDataErrorViewResponse[],
  columns: any[]
) => {
  return source.map((row, idx) => {
    const obj: any = {};
    obj.errorId = row.errorId || null;
    obj.__uid = row.errorId ?? `${row.materialNumber ?? "mat"}__${idx}`;
    // Pre-calc values used in multiple places
    const userConsolidatedSafetyStock = Number(
      parseNumberOrText(row?.["userConsolidatedSafetyStock"])
    );
    const ioConsolidatedSafetyStock = Number(
      parseNumberOrText(row?.["ioConsolidatedSafetyStockRounded"])
    );
    const s4ConsolidatedSafetyStock = Number(
      parseNumberOrText(row?.["s4ConsolidatedSafetyStock"])
    );

    // EU proposal vs IO %
    let euSsProposalVsIoPercent = 0;
    if (
      ioConsolidatedSafetyStock &&
      Number.isFinite(userConsolidatedSafetyStock)
    ) {
      euSsProposalVsIoPercent =
        Math.round((userConsolidatedSafetyStock - ioConsolidatedSafetyStock) /
          ioConsolidatedSafetyStock) *
        100;
    }
    const formattedIoPercent = euSsProposalVsIoPercent.toFixed(0) + "%";
    if (row["euSsProposalVsIoPercent"] !== formattedIoPercent) {
      row["euSsProposalVsIoPercent"] = formattedIoPercent;
    }

    // EU proposal vs S4 %
    let euSsProposalVsS4Percent = 0;
    if (
      s4ConsolidatedSafetyStock &&
      Number.isFinite(userConsolidatedSafetyStock)
    ) {
      euSsProposalVsS4Percent =
        Math.round(((userConsolidatedSafetyStock - s4ConsolidatedSafetyStock) /
          s4ConsolidatedSafetyStock) *
          100);
    }
    row["euSsProposalVsS4Percent"] = euSsProposalVsS4Percent.toFixed(0) + "%";

    // Walk columns and map values
    columns.forEach((col: any) => {
      const assign = (headerName: string, value: any) => {
        const field = toFieldName(headerName);
        obj[field] = value ?? "";
      };

      if (col.children) {
        col.children.forEach((child: any) => {
          switch (child.headerName) {
            case "Approval status":
              assign(child.headerName, row.lineStatus);
              break;
            case "Approver":
              assign(child.headerName, row.approverName);
              break;
            case "MRP controller":
              assign(child.headerName, row.mrpControllerId);
              break;
            case "MRP controller desc":
              assign(child.headerName, row.mrpControllerDescription);
              break;
            case "BCP Flag":
              assign(child.headerName, row.bpcFlag);
              break;
            case "Material Plant":
              assign(child.headerName, row.materialPlant);
              break;
            case "Material Number":
              assign(child.headerName, row.materialNumber);
              break;
            case "Material Description":
              assign(child.headerName, row.materialDescription);
              break;
            case "Total consolidated Safety Stock (weeks)-EU":
              assign(child.headerName, formatOneDecimal(row.userConsolidatedSafetyStock ?? 0));
              break;
            case "Safety Stock (qty)-EU":
              assign(child.headerName, formatValueNoDecimals(row.userSafetyStockQty ?? 0));
              break;
            case "Coverage Profile (weeks)-EU":
              assign(child.headerName, formatOneDecimal(row.userCoverageProfile ?? 0));
              break;
            case "Safety Time (weeks)-EU":
              assign(child.headerName, formatOneDecimal(row.userSafetyTime ?? 0));
              break;
            case "IO Consolidated SS(weeks)":
              assign(
                child.headerName,
                formatOneDecimal(row.ioConsolidatedSafetyStockRounded ?? 0)
              );
              break;
            case "S4 Consolidated SS(weeks)":
              assign(child.headerName, formatOneDecimal(row.s4ConsolidatedSafetyStock ?? 0));
              break;
            case "EU proposal vs IO %":
              assign(child.headerName, row.euSsProposalVsIoPercent ?? "0%");
              break;
            case "EU proposal vs S4 %":
              assign(child.headerName, row.euSsProposalVsS4Percent ?? "0%");
              break;
            case "Reason":
              assign(child.headerName, row.reasonCode);
              break;
            case "Comments":
              assign(child.headerName, row.userComments);
              break;
            default:
              // fallback when header maps directly to field
              obj[child.field] = row[child.field] ?? "";
              break;
          }
        });
      } else {
        // non-grouped column
        switch (col.headerName) {
          case "Approval status":
            obj[col.field] = row.lineStatus ?? "";
            break;
          case "Error Description":
            obj[col.field] = row.errorDesc ?? "";
            break;
          case "MRP controller":
            obj[col.field] = row.mrpControllerId ?? "";
            break;
          case "MRP controller desc":
            obj[col.field] = row.mrpControllerDescription ?? "";
            break;
          case "BCP Flag":
            obj[col.field] = row.bpcFlag ?? "";
            break;
          case "End User action":
            obj[col.field] = row.userSelectedChoice ?? "";
            break;
          case "Material Plant":
            obj[col.field] = row.materialPlant ?? "";
            break;
          case "Material Number":
            obj[col.field] = row.materialNumber ?? "";
            break;
          case "Material Description":
            obj[col.field] = row.materialDescription ?? "";
            break;
          case "Approver":
            obj[col.field] = row.approverName ?? "";
            break;
          case "Reason":
            obj[col.field] = row.userReasonCode ?? "";
            break;
          case "Comments":
            obj[col.field] = row.userComments ?? "";
            break;
          default:
            obj[col.field] = row[col.field] ?? "";
            break;
        }
      }
    });

    return obj;
  });
};